import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  TaskAlt as TaskAltIcon,
} from "@mui/icons-material";
import api from "../services/api";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

export default function Orders() {
  const navigate = useNavigate();
  const checklogin = localStorage.getItem("login");

  if (!checklogin) {
    navigate("/");
  }

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState("");
  const [userDialog, setUserDialog] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (searchText) {
      const filtered = data.filter(
        (order) =>
          order?.Products?.map((product) => product.ProductName)?.some(name => name.toLowerCase().includes(searchText.toLowerCase())) ||
          order?._id?.toLowerCase().includes(searchText.toLowerCase()) ||
          order?.Status?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [searchText, data]);

  const getData = async () => {
    try {
      const res = await api.get("/orders");
      if (res.data) {
        setData(res.data.data);
        setFilteredData(res.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch orders");
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog(true);
    setDeleteItemId(id);
  };

  const confirmDelete = async () => {
    try {
      const res = await api.delete(`/Orders/delete/${deleteItemId}`);
      if (res.data) {
        toast.success(res.data.message);
        getData();
        setDeleteDialog(false);
      }
    } catch (error) {
      toast.error("Failed to delete order");
      setDeleteDialog(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      const res = await api.post("/OrderUpdateStatus", {
        id,
        Statusmsg: status,
      });
      toast.success(res.data.message);
      getData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleViewUser = (userId) => {
    setUserData(userId);
    setUserDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate stats
  const totalOrders = data.length;
  const pendingOrders = data.filter((o) => o.Status === "Pending").length;
  const acceptedOrders = data.filter((o) => o.Status === "Accepted").length;
  const completedOrders = data.filter((o) => o.Status === "Completed").length;
  const rejectedOrders = data.filter((o) => o.Status === "Rejected").length;
  const cancelledOrders = data.filter((o) => o.Status === "Cancelled").length;

  const getStatusColor = (status) => {
    const colors = {
      Pending: { bg: "#fff3e0", color: "#e65100" },
      Accepted: { bg: "#e3f2fd", color: "#1565c0" },
      Completed: { bg: "#e8f5e9", color: "#2e7d32" },
      Rejected: { bg: "#ffebee", color: "#c62828" },
      Cancelled: { bg: "#fff3e0", color: "#f57c00" },
    };
    return colors[status] || { bg: "#f5f5f5", color: "#666" };
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <Header />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: "28px" },
          mt: "64px",
          p: 3,
        }}
      >
        <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight="bold" color="#1a237e">
              Orders Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all customer orders
            </Typography>
          </Box>

          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search orders by product name, order ID, or status..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#757575" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                },
              }}
            />
          </Box>

          {/* Stats Cards */}
          <Stack direction="row" spacing={2} sx={{ mb: 3, overflowX: "auto" }}>
            <Card sx={{ flex: 1, minWidth: 150, p: 2, bgcolor: "#e3f2fd", border: "1px solid #90caf9" }}>
              <Typography variant="body2" color="#1565c0">Total Orders</Typography>
              <Typography variant="h4" fontWeight="bold" color="#0d47a1">{totalOrders}</Typography>
            </Card>
            <Card sx={{ flex: 1, minWidth: 150, p: 2, bgcolor: "#fff3e0", border: "1px solid #ffb74d" }}>
              <Typography variant="body2" color="#e65100">Pending</Typography>
              <Typography variant="h4" fontWeight="bold" color="#e65100">{pendingOrders}</Typography>
            </Card>
            <Card sx={{ flex: 1, minWidth: 150, p: 2, bgcolor: "#e3f2fd", border: "1px solid #64b5f6" }}>
              <Typography variant="body2" color="#1565c0">Accepted</Typography>
              <Typography variant="h4" fontWeight="bold" color="#1565c0">{acceptedOrders}</Typography>
            </Card>
            <Card sx={{ flex: 1, minWidth: 150, p: 2, bgcolor: "#e8f5e9", border: "1px solid #81c784" }}>
              <Typography variant="body2" color="#2e7d32">Completed</Typography>
              <Typography variant="h4" fontWeight="bold" color="#1b5e20">{completedOrders}</Typography>
            </Card>
            <Card sx={{ flex: 1, minWidth: 150, p: 2, bgcolor: "#ffebee", border: "1px solid #e57373" }}>
              <Typography variant="body2" color="#c62828">Rejected</Typography>
              <Typography variant="h4" fontWeight="bold" color="#c62828">{rejectedOrders}</Typography>
            </Card>
            <Card sx={{ flex: 1, minWidth: 150, p: 2, bgcolor: "#fff3e0", border: "1px solid #ffb74d" }}>
              <Typography variant="body2" color="#f57c00">Cancelled</Typography>
              <Typography variant="h4" fontWeight="bold" color="#f57c00">{cancelledOrders}</Typography>
            </Card>
          </Stack>

          {/* Orders Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: "#1565c0" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">S.No</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">Order ID</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">Date</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">Time</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Product Name</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">Price</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">Qty</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">Status</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .reverse()
                  .map((order, index) => {
                    const dateObject = new Date(order.created_at);
                    const hours = dateObject.getHours();
                    const ampm = hours >= 12 ? "PM" : "AM";
                    const minutes = dateObject.getMinutes();
                    const formattedHour = hours % 12 || 12;
                    const formattedTime = `${formattedHour}:${minutes < 10 ? "0" : ""}${minutes} ${ampm}`;
                    const statusStyle = getStatusColor(order.Status);

                    return (
                      <TableRow key={order._id} hover>
                        <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {order._id.slice(-8)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{dateObject.toLocaleDateString()}</TableCell>
                        <TableCell align="center">{formattedTime}</TableCell>
                        <TableCell>{order?.Products?.map((product) => product.ProductName)?.slice(0, 30)}{order?.Products?.map((product) => product.ProductName)?.length > 30 ? '...' : ''}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="#2e7d32">
                            Rs.{order?.Products?.map((product) => product.ProductPrice)?.reduce((acc, curr) => acc + curr, 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={order?.Products?.map((product) => product.ProductQuantity)?.reduce((acc, curr) => acc + curr, 0)} size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={order.Status}
                            size="small"
                            sx={{
                              bgcolor: statusStyle.bg,
                              color: statusStyle.color,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <IconButton
                              size="small"
                              onClick={() => handleViewUser(order.UserId)}
                              sx={{ bgcolor: "#e3f2fd", "&:hover": { bgcolor: "#bbdefb" } }}
                            >
                              <VisibilityIcon fontSize="small" sx={{ color: "#1976d2" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleStatusUpdate(order._id, "Completed")}
                              sx={{ bgcolor: "#e8f5e9", "&:hover": { bgcolor: "#c8e6c9" } }}
                            >
                              <TaskAltIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleStatusUpdate(order._id, "Accepted")}
                              sx={{ bgcolor: "#e3f2fd", "&:hover": { bgcolor: "#bbdefb" } }}
                            >
                              <CheckCircleIcon fontSize="small" sx={{ color: "#1976d2" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleStatusUpdate(order._id, "Rejected")}
                              sx={{ bgcolor: "#ffebee", "&:hover": { bgcolor: "#ffcdd2" } }}
                            >
                              <CancelIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(order._id)}
                              sx={{ bgcolor: "#ffebee", "&:hover": { bgcolor: "#ffcdd2" } }}
                            >
                              <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ bgcolor: "#ffebee", color: "#c62828" }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>Are you sure you want to delete this order? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteDialog(false)} sx={{ color: "#757575" }}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              sx={{ textTransform: "none" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Details Dialog */}
        <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#e3f2fd", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold" color="#1565c0">
              Customer Details
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: "#1976d2" }}>
                  {userData?.FirstName?.charAt(0) || "U"}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {userData?.FirstName} {userData?.LastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userData?.Email}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Shipping Address
                </Typography>
                <Typography variant="body1">{userData?.Address}</Typography>
                <Typography variant="body1">
                  {userData?.City}, {userData?.State}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setUserDialog(false)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
